import React from "react";
import { BarChart, Bar, PieChart, Pie, Cell, ResponsiveContainer, XAxis, Tooltip } from "recharts";
import { PhoneCall, PhoneIncoming, PhoneMissed, Clock, Users, Target } from "lucide-react";

const byHour = [
  { h: "9a", c: 22 }, { h: "11a", c: 40 }, { h: "1p", c: 31 },
  { h: "3p", c: 52 }, { h: "5p", c: 44 }, { h: "7p", c: 28 },
];
const outcomes = [
  { name: "Answered", value: 68, color: "#16A34A" },
  { name: "Missed", value: 18, color: "#DC2626" },
  { name: "Voicemail", value: 14, color: "#F4B400" },
];
const agents = [
  { name: "Priya S.", calls: 128, rate: "94%" },
  { name: "Rahul M.", calls: 112, rate: "91%" },
  { name: "Aisha K.", calls: 98, rate: "88%" },
];

/**
 * Illustrative smart telephony analytics dashboard mockup (demo data only).
 */
export const TelephonyDashboard = () => {
  return (
    <div className="et-card p-5 sm:p-6" data-testid="telephony-dashboard-mock">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-semibold text-slate-900">Telephony Analytics</div>
          <div className="text-[11px] text-slate-400">Illustrative demonstration · not real customer data</div>
        </div>
        <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[10px] font-semibold text-slate-500">Demo</span>
      </div>

      <div className="mt-5 grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi icon={PhoneCall} label="Total Calls" value="1,842" tone="blue" />
        <Kpi icon={PhoneIncoming} label="Answered" value="1,254" tone="green" />
        <Kpi icon={PhoneMissed} label="Missed" value="326" tone="red" />
        <Kpi icon={Clock} label="Avg Duration" value="3m 42s" tone="violet" />
      </div>

      <div className="mt-5 grid lg:grid-cols-2 gap-4">
        <div className="rounded-xl border border-slate-100 p-4">
          <div className="text-xs font-semibold text-slate-700 mb-2">Calls by hour</div>
          <div className="h-32">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={byHour} margin={{ top: 6, right: 6, bottom: 0, left: 0 }}>
                <XAxis dataKey="h" tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                <Tooltip cursor={{ fill: "rgba(37,99,235,0.06)" }} contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }} />
                <Bar dataKey="c" fill="#2563EB" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="rounded-xl border border-slate-100 p-4">
          <div className="text-xs font-semibold text-slate-700 mb-2">Call outcomes</div>
          <div className="flex items-center gap-4">
            <div className="h-28 w-28">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={outcomes} dataKey="value" innerRadius={30} outerRadius={52} paddingAngle={2}>
                    {outcomes.map((o) => <Cell key={o.name} fill={o.color} />)}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="space-y-1.5">
              {outcomes.map((o) => (
                <div key={o.name} className="flex items-center gap-2 text-xs text-slate-600">
                  <span className="h-2.5 w-2.5 rounded-full" style={{ background: o.color }} /> {o.name} · <span className="font-semibold">{o.value}%</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3">
        <Kpi icon={Users} label="Active Agents" value="12" tone="blue" small />
        <Kpi icon={Target} label="Leads / Follow-ups" value="214" tone="amber" small />
      </div>

      <div className="mt-4 overflow-hidden rounded-xl border border-slate-100">
        <table className="w-full text-left">
          <thead className="bg-slate-50">
            <tr className="text-[10px] uppercase tracking-wide text-slate-400">
              <th className="px-4 py-2 font-semibold">Top Agents</th>
              <th className="px-4 py-2 font-semibold">Calls</th>
              <th className="px-4 py-2 font-semibold">Answer rate</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {agents.map((a) => (
              <tr key={a.name} className="text-xs">
                <td className="px-4 py-2.5 font-medium text-slate-800">{a.name}</td>
                <td className="px-4 py-2.5 text-slate-500">{a.calls}</td>
                <td className="px-4 py-2.5 text-green-600 font-semibold">{a.rate}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const toneMap = {
  blue: "bg-blue-50 text-blue-600",
  green: "bg-green-50 text-green-600",
  red: "bg-red-50 text-red-600",
  violet: "bg-violet-50 text-violet-600",
  amber: "bg-amber-50 text-amber-600",
};
const Kpi = ({ icon: Icon, label, value, tone, small }) => (
  <div className="rounded-xl border border-slate-100 p-3">
    <span className={`inline-flex h-8 w-8 items-center justify-center rounded-lg ${toneMap[tone]}`}><Icon className="h-4 w-4" /></span>
    <div className="mt-2 text-[10px] font-medium text-slate-400">{label}</div>
    <div className={`font-display font-bold text-slate-900 ${small ? "text-base" : "text-lg"}`}>{value}</div>
  </div>
);
